import { ExplanationResult, FollowUpMessage, SupportedLanguage } from '@/app/error-explainer-tool/types';

function codeLanguageTag(language: SupportedLanguage): string {
  const map: Record<string, string> = {
    javascript: 'javascript',
    php: 'php',
    python: 'python',
    java: 'java',
    sql: 'sql',
    other: '',
  };
  return map[language] || '';
}

export function generateExplanationMarkdown(
  errorMessage: string,
  language: SupportedLanguage,
  explanation: ExplanationResult,
  provider?: string,
  followUpMessages?: FollowUpMessage[]
): string {
  const tag = codeLanguageTag(language);
  const severityEmoji = explanation.severity === 'critical' ? '🔴' : explanation.severity === 'warning' ? '🟡' : '🔵';

  const sections = [
    `# ${explanation.errorType}`,
    ``,
    `> **Original Error:** \`${errorMessage}\``,
    `> **Language:** ${language.toUpperCase()}`,
    `> **Severity:** ${severityEmoji} ${explanation.severity.toUpperCase()}`,
    `> **Provider:** ${provider || 'AI Router'}`,
    ``,
    `---`,
    ``,
    `## Meaning`,
    ``,
    explanation.meaning,
    ``,
    `---`,
    ``,
    `## Why It Happens`,
    ``,
    explanation.whyItHappens,
    ``,
    `---`,
    ``,
    `## How to Fix It`,
    ``,
    (explanation.howToFix || [])
      .map((step: string, i: number) => `${i + 1}. ${step}`)
      .join('\n'),
    ``,
    `---`,
    ``,
    `## Example Solution`,
    ``,
  ];

  const exampleCode = explanation.exampleCode;
  if (exampleCode?.before) {
    sections.push(`### ❌ Before (Problematic Code)`, ``);
    sections.push('```' + tag);
    sections.push(exampleCode.before);
    sections.push('```');
    sections.push('');
  }

  if (exampleCode?.after) {
    sections.push(`### ✅ After (Fixed Code)`, ``);
    sections.push('```' + tag);
    sections.push(exampleCode.after);
    sections.push('```');
    sections.push('');
  }

  if (exampleCode?.explanation) {
    sections.push(`### 💡 Why This Fix Works`, ``);
    sections.push(exampleCode.explanation);
    sections.push('');
  }

  if (explanation.relatedErrors && explanation.relatedErrors.length > 0) {
    sections.push(
      `---`,
      ``,
      `## Related Errors`,
      ``,
      ...explanation.relatedErrors.map((e: string) => `- ${e}`),
      ``,
    );
  }

  if (followUpMessages && followUpMessages.length > 0) {
    sections.push(
      `---`,
      ``,
      `## Follow-up Discussion`,
      ``,
    );

    for (const msg of followUpMessages) {
      if (msg.role === 'user') {
        sections.push(`> __Q:__ ${msg.content}`, ``);
      } else {
        sections.push(`> __A:__ ${msg.content}`, ``);
      }
    }
  }

  sections.push(
    `---`,
    ``,
    `*Generated by DevError Explainer | ${new Date().toISOString().slice(0, 10)}*`,
  );

  return sections.join('\n');
}

export function downloadMarkdownFile(
  errorMessage: string,
  language: SupportedLanguage,
  explanation: ExplanationResult,
  provider?: string,
  followUpMessages?: FollowUpMessage[]
): void {
  const markdown = generateExplanationMarkdown(errorMessage, language, explanation, provider, followUpMessages);

  const safeName = errorMessage
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);

  const filename = `deverror-${language}-${safeName}.md`;

  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
